import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { generateGuardHoursAlerts } from './utils/guardHoursProcessor';
import { generateShiftAlerts } from './utils/shiftAlertUtils';
import { generateTemplateComparisonAlertsWrapper } from './utils/alertGenerator';
import { formatDateRangeForLog } from './utils/dateUtils';

export const generateScheduleAlerts = async (
  startDate: Date,
  teamId: string | null = null
): Promise<void> => {
  try {
    console.log(`Generating alerts for date: ${format(startDate, 'yyyy-MM-dd')}`);
    
    // Format the date consistently
    const formattedStartDate = format(startDate, 'yyyy-MM-dd');

    // Generate guard hours alerts (guards with less than 38 hours scheduled)
    const guardHoursAlerts = await generateGuardHoursAlerts(startDate, teamId);
    console.log(`Generated ${guardHoursAlerts.length} guard hours alerts`);

    // Generate shift alerts (shifts without assigned guards)
    const shiftAlerts = await generateShiftAlerts(startDate, teamId);
    console.log(`Generated ${shiftAlerts.length} shift alerts`);

    // Generate night-morning shift sequence alerts
    const nightMorningAlerts = await generateNightMorningShiftAlerts(startDate, teamId);
    console.log(`Generated ${nightMorningAlerts.length} night-morning shift alerts`);

    // Generate insufficient rest alerts
    const insufficientRestAlerts = await generateInsufficientRestAlerts(startDate, teamId);
    console.log(`Generated ${insufficientRestAlerts.length} insufficient rest alerts`);

    // Generate visibility status alerts
    const visibilityAlerts = await generateVisibilityStatusAlerts(startDate, teamId);
    console.log(`Generated ${visibilityAlerts.length} visibility status alerts`);

    // Generate template comparison alerts (missing template shifts)
    const templateAlerts = await generateMissingTemplateShiftAlerts(startDate, teamId);
    console.log(`Generated ${templateAlerts.length} missing template shift alerts`);

    // Combine all alerts
    const allAlerts = [
      ...guardHoursAlerts, 
      ...shiftAlerts, 
      ...nightMorningAlerts, 
      ...insufficientRestAlerts, 
      ...visibilityAlerts,
      ...templateAlerts
    ];
    
    if (allAlerts.length === 0) {
      console.log('No alerts to insert');
      return;
    }
    
    // Insert the alerts into the database
    const { data, error } = await supabase
      .from('schedule_alerts')
      .insert(allAlerts);
      
    if (error) {
      console.error('Error inserting schedule alerts:', error);
      throw error;
    }
    
    console.log(`Successfully inserted ${allAlerts.length} alerts`);
  } catch (error) {
    console.error('Error generating schedule alerts:', error);
    throw error;
  }
};

// Generate night-morning shift sequence alerts
const generateNightMorningShiftAlerts = async (weekStartDate: Date, teamId: string | null = null) => {
  const alerts = [];
  
  try {
    const teams = teamId 
      ? [{ id: teamId, name: 'Selected Team' }] 
      : await getTeams();

    for (const team of teams) {
      // Get team schedules for the week
      const { data: schedules, error } = await supabase
        .from('team_schedules')
        .select('*')
        .eq('team_id', team.id)
        .gte('start_date', format(weekStartDate, 'yyyy-MM-dd'))
        .lte('start_date', format(new Date(weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'));

      if (error) {
        console.error(`Error fetching schedules for team ${team.name}:`, error);
        continue;
      }

      if (!schedules || schedules.length === 0) continue;

      // Get team members
      const { data: teamMembers } = await supabase
        .from('team_members')
        .select('profile_id')
        .eq('team_id', team.id);

      if (!teamMembers) continue;

      // Get profiles for team members
      const profileIds = teamMembers.map(tm => tm.profile_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .in('id', profileIds);

      if (!profiles) continue;

      // Check for night-morning sequences for each guard
      for (const profile of profiles) {
        const guardSchedules = schedules
          .filter(s => s.assigned_guards && s.assigned_guards.includes(profile.id))
          .sort((a, b) => fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime());

        for (let i = 0; i < guardSchedules.length - 1; i++) {
          const currentShift = guardSchedules[i];
          const nextShift = guardSchedules[i + 1];

          const currentEndHour = fromLocalISOString(currentShift.end_date).getHours();
          const nextStartHour = fromLocalISOString(nextShift.start_date).getHours();

          // Night shift ends after 22:00 or before 6:00, morning shift starts before 8:00
          const isNightShift = currentEndHour >= 22 || currentEndHour < 6;
          const isMorningShift = nextStartHour < 8;

          if (isNightShift && isMorningShift) {
            const guardName = profile.full_name || `${profile.first_name} ${profile.last_name}`;
            const currentShiftDate = format(fromLocalISOString(currentShift.start_date), 'MMM dd');
            const nextShiftDate = format(fromLocalISOString(nextShift.start_date), 'MMM dd');
            
            alerts.push({
              team_id: team.id,
              team_name: `${guardName} - ${team.name}`,
              alert_type: 'night_morning_shift',
              alert_message: `${guardName} has a night shift followed by a morning shift (${currentShiftDate} → ${nextShiftDate})`,
              week_start_date: format(weekStartDate, 'yyyy-MM-dd'),
              guard_id: profile.id,
              created_at: new Date().toISOString(),
              is_resolved: false,
              content: `Guard: ${guardName}\nTeam: ${team.name}\nNight shift on ${currentShiftDate} ending at ${format(fromLocalISOString(currentShift.end_date), 'HH:mm')}\nMorning shift on ${nextShiftDate} starting at ${format(fromLocalISOString(nextShift.start_date), 'HH:mm')}`
            });
          }
        }
      }
    }
  } catch (error) {
    console.error('Error generating night-morning shift alerts:', error);
  }

  return alerts;
};

// Generate insufficient rest alerts
const generateInsufficientRestAlerts = async (weekStartDate: Date, teamId: string | null = null) => {
  const alerts = [];
  
  try {
    const teams = teamId 
      ? [{ id: teamId, name: 'Selected Team' }] 
      : await getTeams();

    for (const team of teams) {
      // Get team schedules for the week
      const { data: schedules, error } = await supabase
        .from('team_schedules')
        .select('*')
        .eq('team_id', team.id)
        .gte('start_date', format(weekStartDate, 'yyyy-MM-dd'))
        .lte('start_date', format(new Date(weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'));

      if (error) {
        console.error(`Error fetching schedules for team ${team.name}:`, error);
        continue;
      }

      if (!schedules || schedules.length === 0) continue;

      // Get team members
      const { data: teamMembers } = await supabase
        .from('team_members')
        .select('profile_id')
        .eq('team_id', team.id);

      if (!teamMembers) continue;

      // Get profiles for team members
      const profileIds = teamMembers.map(tm => tm.profile_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .in('id', profileIds);

      if (!profiles) continue;

      // Check for insufficient rest between shifts
      for (const profile of profiles) {
        const guardSchedules = schedules
          .filter(s => s.assigned_guards && s.assigned_guards.includes(profile.id))
          .sort((a, b) => fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime());

        for (let i = 0; i < guardSchedules.length - 1; i++) {
          const currentShift = guardSchedules[i];
          const nextShift = guardSchedules[i + 1];

          const restTime = fromLocalISOString(nextShift.start_date).getTime() - fromLocalISOString(currentShift.end_date).getTime();
          const restHours = restTime / (1000 * 60 * 60);

          if (restHours < 11) {
            const guardName = profile.full_name || `${profile.first_name} ${profile.last_name}`;
            const currentShiftDate = format(fromLocalISOString(currentShift.start_date), 'MMM dd');
            const nextShiftDate = format(fromLocalISOString(nextShift.start_date), 'MMM dd');
            
            alerts.push({
              team_id: team.id,
              team_name: `${guardName} - ${team.name}`,
              alert_type: 'insufficient_rest',
              alert_message: `${guardName} has insufficient rest between shifts (${Math.round(restHours * 10) / 10} hours) on ${currentShiftDate} → ${nextShiftDate}`,
              week_start_date: format(weekStartDate, 'yyyy-MM-dd'),
              guard_id: profile.id,
              created_at: new Date().toISOString(),
              is_resolved: false,
              content: `Guard: ${guardName}\nTeam: ${team.name}\nShift 1: ${currentShiftDate} ending at ${format(fromLocalISOString(currentShift.end_date), 'HH:mm')}\nShift 2: ${nextShiftDate} starting at ${format(fromLocalISOString(nextShift.start_date), 'HH:mm')}\nRest time: ${Math.round(restHours * 10) / 10} hours (minimum 11 hours recommended)`
            });
          }
        }
      }
    }
  } catch (error) {
    console.error('Error generating insufficient rest alerts:', error);
  }

  return alerts;
};

// Generate visibility status alerts
const generateVisibilityStatusAlerts = async (weekStartDate: Date, teamId: string | null = null) => {
  const alerts = [];
  
  try {
    const teams = teamId 
      ? [{ id: teamId, name: 'Selected Team' }] 
      : await getTeams();

    for (const team of teams) {
      // Check if team schedule is visible for this week using the week_visibility table
      const { data: visibilityData } = await supabase
        .from('week_visibility')
        .select('is_visible')
        .eq('team_id', team.id)
        .eq('week_start_date', format(weekStartDate, 'yyyy-MM-dd'))
        .single();

      // If no visibility setting exists, default to visible (true)
      // Only create alert if explicitly set to not visible (false)
      if (visibilityData && visibilityData.is_visible === false) {
        const weekDisplay = format(weekStartDate, 'MMM dd');
        
        alerts.push({
          team_id: team.id,
          team_name: team.name,
          alert_type: 'visibility_status',
          alert_message: `Team schedule is not visible to guards for week of ${weekDisplay}`,
          week_start_date: format(weekStartDate, 'yyyy-MM-dd'),
          created_at: new Date().toISOString(),
          is_resolved: false,
          content: `Week: ${weekDisplay}\nSchedule visibility is disabled for this team and week. Guards cannot see their schedules.`
        });
      }
    }
  } catch (error) {
    console.error('Error generating visibility status alerts:', error);
  }

  return alerts;
};

// Generate missing template shift alerts
const generateMissingTemplateShiftAlerts = async (weekStartDate: Date, teamId: string | null = null) => {
  const alerts = [];
  
  try {
    const teams = teamId 
      ? [{ id: teamId, name: 'Selected Team' }] 
      : await getTeams();

    for (const team of teams) {
      // Get shift templates for this team
      const { data: templates, error } = await supabase
        .from('shift_templates')
        .select('*')
        .eq('team_id', team.id);

      if (error || !templates || templates.length === 0) continue;

      // Get actual schedules for the week
      const { data: schedules } = await supabase
        .from('team_schedules')
        .select('*')
        .eq('team_id', team.id)
        .gte('start_date', format(weekStartDate, 'yyyy-MM-dd'))
        .lte('start_date', format(new Date(weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'));

      // Check if template shifts are missing from actual schedules
      for (const template of templates) {
        try {
          // Safely parse the schedule_data
          const scheduleData = typeof template.schedule_data === 'string' 
            ? JSON.parse(template.schedule_data) 
            : template.schedule_data;

          if (scheduleData && typeof scheduleData === 'object' && 'shifts' in scheduleData) {
            const templateShifts = Array.isArray(scheduleData.shifts) 
              ? scheduleData.shifts 
              : [scheduleData.shifts];

            for (const templateShift of templateShifts) {
              if (templateShift && typeof templateShift === 'object') {
                // Check if this template shift exists in actual schedules
                const hasMatchingSchedule = schedules?.some(schedule => {
                  const scheduleTime = format(fromLocalISOString(schedule.start_date), 'HH:mm');
                  const templateTime = templateShift.start_time || templateShift.start_date;
                  return scheduleTime === templateTime && schedule.title === templateShift.title;
                });

                if (!hasMatchingSchedule) {
                  const weekDisplay = format(weekStartDate, 'MMM dd');
                  const shiftTitle = templateShift.title || 'Unnamed';
                  
                  alerts.push({
                    team_id: team.id,
                    team_name: team.name,
                    alert_type: 'missing_template_shifts',
                    alert_message: `Template shift "${shiftTitle}" is missing from actual schedule for week of ${weekDisplay}`,
                    week_start_date: format(weekStartDate, 'yyyy-MM-dd'),
                    created_at: new Date().toISOString(),
                    is_resolved: false,
                    content: `Week: ${weekDisplay}\nTemplate: "${template.name}"\nMissing Shift: "${shiftTitle}"\nThis template shift is not present in the actual weekly schedule.`
                  });
                }
              }
            }
          }
        } catch (parseError) {
          console.error(`Error parsing schedule_data for template ${template.id}:`, parseError);
          continue;
        }
      }
    }
  } catch (error) {
    console.error('Error generating missing template shift alerts:', error);
  }

  return alerts;
};

const getTeams = async () => {
  const { data: teams, error } = await supabase
    .from('teams')
    .select('id, name');
    
  if (error) {
    console.error('Error fetching teams:', error);
    return [];
  }
  
  return teams || [];
};
